import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import auth from '../Auth/Auth';

const CartContext = createContext();
const marketingBackendUrl = import.meta.env.VITE_MARKETING_BACKEND_URL;

export { CartContext };
export const useCart = () => useContext(CartContext);

// Helper to convert Arabic-Indic numerals to Western numerals
function parseArabicNumber(str) {
  if (typeof str === 'string') {
    return Number(str.replace(/[\u0660-\u0669]/g, d => '٠١٢٣٤٥٦٧٨٩'.indexOf(d)));
  }
  return Number(str);
}

// Helper to get guest cartId from localStorage
function getGuestCartId() {
  return localStorage.getItem('guestCartId');
}
function setGuestCartId(cartId) {
  localStorage.setItem('guestCartId', cartId);
}
function clearGuestCartId() {
  localStorage.removeItem('guestCartId');
}

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [], totalAmount: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const userEmail = localStorage.getItem("useremailidis");
  const token = auth.getAuthData()?.token || localStorage.getItem('token');

  // Fetch cart on mount and when userEmail or token changes
  useEffect(() => {
      fetchCart();
  }, [userEmail, token]);

  const fetchCart = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!userEmail) {
        // Guest user: fetch from backend using cartId
        const cartId = getGuestCartId();
        if (!cartId) {
          setCart({ items: [], totalAmount: 0 });
          return;
        }
        const response = await axios.get(`${marketingBackendUrl}/maflam/cart/guest/${cartId}`);
        setCart(response.data);
        return;
      }
      
      // Logged-in user: use token for authentication
      const response = await axios.get(`${marketingBackendUrl}/maflam/cart/${userEmail}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      // If user cart is empty but there's a guest cart ID, try to migrate
      if ((!response.data.items || response.data.items.length === 0) && getGuestCartId()) {
        console.log("User cart is empty but guest cart exists, attempting migration...");
        try {
          const guestCartId = getGuestCartId();
          await axios.post(`${marketingBackendUrl}/maflam/cart/migrate`, {
            guestCartId,
            userEmail
          });
          
          // Fetch the cart again after migration
          const updatedResponse = await axios.get(`${marketingBackendUrl}/maflam/cart/${userEmail}`, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          });
          setCart(updatedResponse.data);
          
          // Clear guest cart ID
          clearGuestCartId();
          return;
        } catch (migrationError) {
          console.error("Error during automatic cart migration:", migrationError);
          // Continue with empty cart if migration fails
        }
      }
      
      setCart(response.data);
    } catch (error) {
      setError(error.response?.data?.message || error.message);
      setCart({ items: [], totalAmount: 0 });
    } finally {
      setLoading(false);
    }
  };

  const addToCart = async (course) => {
    try {
      setLoading(true);
      setError(null);
      let parsedPriceArabic = parseFloat(course.CourseCostArabic);
      let parsedPriceEnglish = parseFloat(course.CourseCostEnglish);
      if (isNaN(parsedPriceArabic)) parsedPriceArabic = 0;
      if (isNaN(parsedPriceEnglish)) parsedPriceEnglish = 0;
      const cartItem = {
        courseId: course._id,
        courseName: course.CourseNameArabic || course.CourseNameEnglish || course.CourseName,
        courseNameArabic: course.CourseNameArabic || course.CourseNameEnglish || course.CourseName,
        courseNameEnglish: course.CourseNameEnglish || course.CourseNameArabic || course.CourseName,
        price: parsedPriceEnglish || parsedPriceArabic || 0,
        priceArabic: parsedPriceArabic,
        priceEnglish: parsedPriceEnglish,
        thumbnail: course.ThumbnailEnglish || course.ThumbnailArabic || course.Thumbnail || "https://maflamlmsnew.s3.me-central-1.amazonaws.com/marketingphotos/default-course.png",
        description: course.CourseDescriptionArabic || course.CourseDescriptionEnglish || course.CourseDescription,
        descriptionArabic: course.CourseDescriptionArabic || course.CourseDescriptionEnglish || course.CourseDescription,
        descriptionEnglish: course.CourseDescriptionEnglish || course.CourseDescriptionArabic || course.CourseDescription
      };
      if (!userEmail) {
        // Guest user: use backend guest cart
        let cartId = getGuestCartId();
        if (!cartId) {
          // Create guest cart
          const createRes = await axios.post(`${marketingBackendUrl}/maflam/cart/guest`);
          cartId = createRes.data.cartId;
          setGuestCartId(cartId);
        }
        // Add to guest cart
        const response = await axios.post(`${marketingBackendUrl}/maflam/cart/guest/add`, { cartId, ...cartItem });
        if (response.data.success) {
          // Check if the response indicates course is already in cart
          if (response.data.message && response.data.message.includes("already in cart")) {
            setCart(response.data.cart);
            throw new Error("Course Already in Cart");
          } else {
            setCart(response.data.cart);
          }
        } else {
          throw new Error(response.data.message || "Failed to add to cart");
        }
        return;
      }
      // Logged-in user logic (unchanged)
      const requestData = {
        userEmail,
        courseId: course._id,
        courseName: course.CourseNameArabic || course.CourseNameEnglish || course.CourseName,
        courseNameArabic: course.CourseNameArabic || course.CourseNameEnglish || course.CourseName,
        courseNameEnglish: course.CourseNameEnglish || course.CourseNameArabic || course.CourseName,
        price: parsedPriceEnglish || parsedPriceArabic || 0,
        priceArabic: parsedPriceArabic,
        priceEnglish: parsedPriceEnglish,
        thumbnail: course.ThumbnailArabic || course.ThumbnailEnglish || course.Thumbnail,
        description: course.CourseDescriptionArabic || course.CourseDescriptionEnglish || course.CourseDescription,
        descriptionArabic: course.CourseDescriptionArabic || course.CourseDescriptionEnglish || course.CourseDescription,
        descriptionEnglish: course.CourseDescriptionEnglish || course.CourseDescriptionArabic || course.CourseDescription
      };
      const response = await axios.post(`${marketingBackendUrl}/maflam/cart/add`, requestData, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      if (response.data.success) {
        // Check if the response indicates course is already in cart
        if (response.data.message && response.data.message.includes("already in cart")) {
          setCart(response.data.cart);
          throw new Error("Course Already in Cart");
        } else {
          setCart(response.data.cart);
        }
      } else {
        throw new Error(response.data.message || "Failed to add to cart");
      }
    } catch (error) {
      setError(error.response?.data?.message || error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (productId) => {
    try {
      setLoading(true);
      setError(null);
      if (!userEmail) {
        // Guest user: remove from backend guest cart
        const cartId = getGuestCartId();
        if (!cartId) return;
        const response = await axios.delete(`${marketingBackendUrl}/maflam/cart/guest/${cartId}/${productId}`);
        setCart(response.data);
        return;
      }
      // Logged-in user logic (unchanged)
        const response = await axios.delete(`${marketingBackendUrl}/maflam/cart/${userEmail}/${productId}`);
        if (response.data) {
          setCart(response.data);
      }
    } catch (error) {
      setError(error.response?.data?.message || error.message);
    } finally {
      setLoading(false);
    }
  };

  const clearCart = async () => {
    try {
      setLoading(true);
      setError(null);
      if (!userEmail) {
        // Guest user: clear backend guest cart
        const cartId = getGuestCartId();
        if (!cartId) return;
        await axios.delete(`${marketingBackendUrl}/maflam/cart/guest/clear/${cartId}`);
        setCart({ items: [], totalAmount: 0 });
        clearGuestCartId();
        return;
      }
      // Logged-in user logic (unchanged)
      await axios.delete(`${marketingBackendUrl}/maflam/cart/clear/${userEmail}`);
      setCart({ items: [], totalAmount: 0 });
    } catch (error) {
      setError(error.response?.data?.message || error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        loading,
        error,
        addToCart,
        removeFromCart,
        clearCart,
        fetchCart
      }}
    >
      {children}
    </CartContext.Provider>
  );
}; 